import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, History, FlaskConical, Calendar, Award, Activity } from 'lucide-react';
import { useDashboard } from '@/api/hooks/useDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

const stagger = {
  animate: { transition: { staggerChildren: 0.06 } },
};

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function tierBadge(tier?: string) {
  if (!tier) return null;
  const variants: Record<string, 'default' | 'success' | 'warning' | 'destructive'> = {
    optimal: 'success',
    normal: 'secondary' as 'success',
    suboptimal: 'warning',
    critical: 'destructive',
  };
  return (
    <Badge variant={variants[tier] ?? 'secondary'} className="capitalize">
      {tier}
    </Badge>
  );
}

export function DashboardPage() {
  const { data, isLoading } = useDashboard();

  const firstName = data?.profile?.first_name;
  const greeting = firstName
    ? `Welcome back, ${firstName}`
    : 'Welcome to MensResearch';

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <motion.div variants={stagger} initial="initial" animate="animate">
        {/* Greeting */}
        <motion.div variants={fadeUp} className="mb-8">
          <h1 className="text-2xl font-bold text-navy">{greeting}</h1>
          <p className="mt-1 text-gray-500">
            Your bloodwork analysis dashboard
          </p>
        </motion.div>

        {/* Stats row */}
        <motion.div variants={fadeUp} className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-copper/10">
              <Calendar className="h-5 w-5 text-copper" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Last Upload</p>
              {isLoading ? (
                <Skeleton className="h-5 w-24 mt-1" />
              ) : (
                <p className="text-sm font-semibold text-navy">
                  {data?.recentUploads?.[0]
                    ? formatDate(data.recentUploads[0].created_at)
                    : 'No uploads yet'}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50">
              <Award className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Protocol</p>
              {isLoading ? (
                <Skeleton className="h-5 w-20 mt-1" />
              ) : (
                <p className="text-sm font-semibold text-navy capitalize">
                  {data?.currentProtocol?.status ?? 'Not set'}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
              <Activity className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Recommendations</p>
              {isLoading ? (
                <Skeleton className="h-5 w-12 mt-1" />
              ) : (
                <p className="text-sm font-semibold text-navy">
                  {data?.pendingRecommendations ?? 0} pending
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Quick actions */}
        <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link to="/upload" className="group">
            <div className="rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-copper/30 hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-copper/10 mb-4 group-hover:bg-copper/15 transition-colors">
                <Upload className="h-6 w-6 text-copper" />
              </div>
              <h3 className="font-semibold text-navy">Upload New Labs</h3>
              <p className="mt-1 text-sm text-gray-500">
                Upload your bloodwork PDF or image for analysis
              </p>
            </div>
          </Link>

          <Link to="/history" className="group">
            <div className="rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-slate-brand/30 hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-brand/10 mb-4 group-hover:bg-slate-brand/15 transition-colors">
                <History className="h-6 w-6 text-slate-brand" />
              </div>
              <h3 className="font-semibold text-navy">View History</h3>
              <p className="mt-1 text-sm text-gray-500">
                Track your biomarker trends over time
              </p>
            </div>
          </Link>

          <Link to="/protocol" className="group">
            <div className="rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-emerald-400/30 hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 mb-4 group-hover:bg-emerald-100 transition-colors">
                <FlaskConical className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-navy">Update Protocol</h3>
              <p className="mt-1 text-sm text-gray-500">
                Log your current supplements, TRT, or peptide protocol
              </p>
            </div>
          </Link>
        </motion.div>

        {/* Recent results */}
        <motion.div variants={fadeUp}>
          <Card>
            <CardHeader>
              <CardTitle>Recent Results</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : !data?.recentUploads?.length ? (
                <div className="py-12 text-center">
                  <Upload className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No uploads yet</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Upload your first lab results to get started
                  </p>
                  <Link
                    to="/upload"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-copper hover:text-copper-dark transition-colors"
                  >
                    <Upload className="h-4 w-4" />
                    Upload Labs
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {data.recentUploads.slice(0, 5).map((upload) => (
                    <Link
                      key={upload.id}
                      to={`/results/${upload.id}`}
                      className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100">
                          <Activity className="h-4 w-4 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy">
                            {upload.filename || 'Lab Results'}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDate(upload.created_at)}
                            {upload.marker_count
                              ? ` \u00b7 ${upload.marker_count} markers`
                              : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {tierBadge(upload.tier)}
                        <Badge
                          variant={
                            upload.status === 'complete'
                              ? 'success'
                              : upload.status === 'processing'
                              ? 'warning'
                              : 'secondary'
                          }
                          className="capitalize"
                        >
                          {upload.status}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </div>
  );
}
